import * as React from 'react';

// PACKAGES
import { Formik, Form, Field, ErrorMessage, FieldArray } from 'formik';
import * as Yup from 'yup';

// COMPONENTS
import Button from '../../components/Button';
import DisplayDateTime from '../../components/DisplayDateTime';
import DateTimeInput from '../../components/DateTimeInput';
import GlobalModal from '../../components/GlobalModal';
import AddMedicationModal from './Modals/AddMedicationModal';
import AddMedicalPersonnelModal from './Modals/AddMedicalPersonnelModal';
import AddEmergencyServiceModal from './Modals/AddEmergencyServiceModal';

// ICONS
import { ReactComponent as PlusIcon } from '../../assets/svg/plus.svg';

// TYPES
import { IncidentFormTypes } from './IncidentSubmit';
import { IncidentEditUser } from '../../api/models';

export interface StepTwoTypes {
	incidentFormData: IncidentFormTypes;
	setIncidentFormData: React.Dispatch<React.SetStateAction<IncidentFormTypes>>;
	setActiveStep: React.Dispatch<React.SetStateAction<string>>;
}

import { IncidentRecipientsUser } from '../../api/models/incident-recipients-user';

const StepTwo: React.FC<StepTwoTypes> = ({ incidentFormData, setIncidentFormData, setActiveStep }) => {
	// State to manage Modal
	const [showAddMedicationModal, setShowAddMedicationModal] = React.useState<boolean>(false);
	const [showAddMedicalPersonnelModal, setShowAddMedicalPersonnelModal] = React.useState<boolean>(false);
	const [showAddEmergencyServiceModal, setShowAddEmergencyServiceModal] = React.useState<boolean>(false);

	const initialValuesStepTwo = {
		firstAidAdministered: incidentFormData.firstAidAdministered || false,
		firstAid: incidentFormData.firstAid || '',
		medicationAdministered: incidentFormData.medicationAdministered || false,
		medication: incidentFormData.medication || [],
		medicalPersonnelContacted: incidentFormData.medicalPersonnelContacted || false,
		medicalPersonnel: incidentFormData.medicalPersonnel || [],
		emergencyServiceContacted: incidentFormData.emergencyServiceContacted || false,
		emergencyServices: incidentFormData.emergencyServices || [],
		takenToHospital: incidentFormData.takenToHospital || false,
		hospital: incidentFormData.hospital || '',
		dateAndTimeTakenToHospital: incidentFormData.dateAndTimeTakenToHospital || '',
		personTakenToHospital: incidentFormData.personTakenToHospital || '',
		relationshipToChild: incidentFormData.relationshipToChild || '',
		otherActions: incidentFormData.otherActions || false,
		otherActionsTaken: incidentFormData.otherActionsTaken || ''
	};

	const validationSchemaStepTwo = Yup.object({
		firstAid: Yup.string().when('firstAidAdministered', {
			is: true,
			then: Yup.string().required('Required')
		}),
		medication: Yup.array().when('medicationAdministered', {
			is: true,
			then: Yup.array().min(1, 'Required')
		}),
		medicalPersonnel: Yup.array().when('medicalPersonnelContacted', {
			is: true,
			then: Yup.array().min(1, 'Required')
		}),
		emergencyServices: Yup.array().when('emergencyServiceContacted', {
			is: true,
			then: Yup.array().min(1, 'Required')
		}),
		// hospital: Yup.string().when('takenToHospital', {
		// 	is: true,
		// 	then: Yup.string().required('Required')
		// }),
		dateAndTimeTakenToHospital: Yup.string().when('takenToHospital', {
			is: true,
			then: Yup.string().required('Required')
		}),
		personTakenToHospital: Yup.string().when('takenToHospital', {
			is: true,
			then: Yup.string().required('Required')
		}),
		relationshipToChild: Yup.string().when('takenToHospital', {
			is: true,
			then: Yup.string().required('Required')
		}),
		otherActionsTaken: Yup.string().when('otherActions', {
			is: true,
			then: Yup.string().required('Required')
		})
	});

	// SUBMIT LOGIC
	const onSubmitStepTwo = (values: any) => {
		setActiveStep('step3');

		setIncidentFormData((prevState) => ({
			...prevState,

			firstAidAdministered: values.firstAidAdministered,
			firstAid: values.firstAid,
			medicationAdministered: values.medicationAdministered,
			medication: values.medication,
			medicalPersonnelContacted: values.medicalPersonnelContacted,
			medicalPersonnel: values.medicalPersonnel,
			emergencyServiceContacted: values.emergencyServiceContacted,
			emergencyServices: values.emergencyServices,
			takenToHospital: values.takenToHospital,
			hospital: values.hospital,
			dateAndTimeTakenToHospital: values.dateAndTimeTakenToHospital,
			personTakenToHospital: values.personTakenToHospital,
			relationshipToChild: values.relationshipToChild,
			otherActions: values.otherActions,
			otherActionsTaken: values.otherActionsTaken
		}));

		window.scrollTo(0, 0);
	};

	return (
		<Formik
			initialValues={initialValuesStepTwo}
			validationSchema={validationSchemaStepTwo}
			onSubmit={onSubmitStepTwo}
			enableReinitialize
		>
			{(formik) => {
				return (
					<Form translate="yes" className="general-form">
						<div className="general-form-layout-wrapper">
							<div className="form-main w-100">
								<h3>First aid</h3>

								<div className="general-divider mbtom-30"></div>

								<div className="form-group w-60 mbtom-40">
									<label className="label">Was first aid administered?</label>

									<div className="form-field radio-group">
										<div className="radio-row mbtom-0">
											<Field
												name="firstAidAdministered"
												id="firstAidAdministered"
												type="radio"
												value={true}
												checked={formik.values.firstAidAdministered === true}
												onChange={() => formik.setFieldValue('firstAidAdministered', true)}
											/>

											<span className="radio-option">Yes</span>
										</div>

										<div className="radio-row mbtom-0">
											<Field
												name="firstAidAdministered"
												id="firstAidAdministered"
												type="radio"
												value={false}
												checked={formik.values.firstAidAdministered === false}
												onChange={() => formik.setFieldValue('firstAidAdministered', false)}
											/>

											<span className="radio-option">No</span>
										</div>

										<ErrorMessage name="firstAidAdministered" component="div" className="error-message" />
									</div>

									{formik.values.firstAidAdministered === true && (
										<div className="form-group mbtom-40">
											<label htmlFor="firstAid" className="label">
												Please describe the first aid administered
											</label>

											<div className="form-field">
												<Field as="textarea" name="firstAid" id="firstAid" type="text" className="form-input" />

												<ErrorMessage name="firstAid" component="div" className="error-message" />
											</div>
										</div>
									)}
								</div>

								<h3>Medication</h3>

								<p className="sub-grey-text mtop-10">
									Please record the name of the medication, the dosage, who administered the medication and the date and
									time of administration
								</p>

								<div className="general-divider mbtom-30"></div>

								<div className="form-group w-60 mbtom-40">
									<label className="label">Was any medication administered?</label>

									<div className="form-field radio-group">
										<div className="radio-row mbtom-0">
											<Field
												name="medicationAdministered"
												id="medicationAdministered"
												type="radio"
												value={true}
												checked={formik.values.medicationAdministered === true}
												onChange={() => formik.setFieldValue('medicationAdministered', true)}
											/>

											<span className="radio-option">Yes</span>
										</div>

										<div className="radio-row mbtom-0">
											<Field
												name="medicationAdministered"
												id="medicationAdministered"
												type="radio"
												value={false}
												checked={formik.values.medicationAdministered === false}
												onChange={() => formik.setFieldValue('medicationAdministered', false)}
											/>

											<span className="radio-option">No</span>
										</div>

										<ErrorMessage name="medicationAdministered" component="div" className="error-message" />
									</div>
								</div>

								{formik.values.medicationAdministered === true && (
									<div className="form-group w-60 mbtom-40">
										<label>List of medication</label>

										<div className="general-divider mbtom-0"></div>

										<FieldArray name="medication">
											{(props) => {
												const { push, remove, form } = props;
												const { values } = form;
												const { medication } = values;

												return (
													<>
														{medication?.length > 0 &&
															medication.map((item: any, index: number) => {
																return (
																	<div className="incident-detail-row row" key={index}>
																		<div className="col-12 pdl-0">
																			<p className="name">{item.name}</p>
																		</div>

																		<div className="col-4 pdl-0">
																			<p>Dosage of {item.dosage}</p>
																		</div>

																		<div className="col-5">
																			<p>by {item.personAdministered}</p>
																		</div>

																		<div className="col-3 text-right pdr-0">
																			<Button
																				onClick={() => {
																					remove(index);
																				}}
																				className="btn-text pd-0 "
																			>
																				Remove
																			</Button>
																		</div>

																		<div className="col-4 pdl-0">
																			<p>
																				<DisplayDateTime>{item.dateAndTime}</DisplayDateTime>
																			</p>
																		</div>

																		<div className="col-5">
																			<p>{item.role}</p>
																		</div>
																	</div>
																);
															})}

														<Button
															onClick={() => {
																setIncidentFormData((prevState) => ({
																	...prevState,

																	firstAidAdministered: values.firstAidAdministered,
																	firstAid: values.firstAid,
																	medicationAdministered: values.medicationAdministered,
																	medication: values.medication,
																	medicalPersonnelContacted: values.medicalPersonnelContacted,
																	medicalPersonnel: values.medicalPersonnel,
																	emergencyServiceContacted: values.emergencyServiceContacted,
																	emergencyServices: values.emergencyServices,
																	takenToHospital: values.takenToHospital,
																	hospital: values.hospital,
																	dateAndTimeTakenToHospital: values.dateAndTimeTakenToHospital,
																	personTakenToHospital: values.personTakenToHospital,
																	relationshipToChild: values.relationshipToChild,
																	otherActions: values.otherActions,
																	otherActionsTaken: values.otherActionsTaken
																}));

																setShowAddMedicationModal(true);
															}}
															type="button"
															className="add-witness mbtom-0"
														>
															<PlusIcon className="icon" />
															Add medication
														</Button>
													</>
												);
											}}
										</FieldArray>

										<ErrorMessage name="medication">
											{(error) => <div className="form-validation-error">{error}</div>}
										</ErrorMessage>
									</div>
								)}

								<h3>Medical personnel</h3>

								<p className="sub-grey-text mtop-10">
									Please record the name and contact details of the medical personnel and the date and time they were
									contacted
								</p>

								<div className="general-divider mbtom-30"></div>

								<div className="form-group w-60 mbtom-40">
									<label className="label">Were any medical personnel contacted?</label>

									<div className="form-field radio-group">
										<div className="radio-row mbtom-0">
											<Field
												name="medicalPersonnelContacted"
												id="medicalPersonnelContacted"
												type="radio"
												value={true}
												checked={formik.values.medicalPersonnelContacted === true}
												onChange={() => formik.setFieldValue('medicalPersonnelContacted', true)}
											/>

											<span className="radio-option">Yes</span>
										</div>

										<div className="radio-row mbtom-0">
											<Field
												name="medicalPersonnelContacted"
												id="medicalPersonnelContacted"
												type="radio"
												value={false}
												checked={formik.values.medicalPersonnelContacted === false}
												onChange={() => formik.setFieldValue('medicalPersonnelContacted', false)}
											/>

											<span className="radio-option">No</span>
										</div>

										<ErrorMessage name="medicalPersonnelContacted" component="div" className="error-message" />
									</div>
								</div>

								{formik.values.medicalPersonnelContacted === true && (
									<div className="form-group w-60 mbtom-40">
										<label>List of medical personnel</label>

										<div className="general-divider mbtom-0"></div>

										<FieldArray name="medicalPersonnel">
											{(props) => {
												const { push, remove, form } = props;
												const { values } = form;
												const { medicalPersonnel } = values;

												return (
													<>
														{medicalPersonnel?.length > 0 &&
															medicalPersonnel.map((item: any, index: number) => {
																return (
																	<div className="incident-detail-row row" key={index}>
																		<div className="col-12 pdl-0">
																			<p className="name">{item.name}</p>
																		</div>

																		<div className="col-4 pdl-0">
																			<p>{item.role}</p>
																		</div>

																		<div className="col-5"></div>

																		<div className="col-3 text-right pdr-0">
																			<Button
																				onClick={() => {
																					remove(index);
																				}}
																				className="btn-text pd-0"
																			>
																				Remove
																			</Button>
																		</div>

																		<div className="col-5 pdl-0">
																			<p>
																				<DisplayDateTime>{item.dateAndTime}</DisplayDateTime>
																			</p>
																		</div>

																		<div className="col-4">
																			<p>{item.phoneOrEmail}</p>
																		</div>
																	</div>
																);
															})}

														<Button
															onClick={() => {
																setIncidentFormData((prevState) => ({
																	...prevState,

																	firstAidAdministered: values.firstAidAdministered,
																	firstAid: values.firstAid,
																	medicationAdministered: values.medicationAdministered,
																	medication: values.medication,
																	medicalPersonnelContacted: values.medicalPersonnelContacted,
																	medicalPersonnel: values.medicalPersonnel,
																	emergencyServiceContacted: values.emergencyServiceContacted,
																	emergencyServices: values.emergencyServices,
																	takenToHospital: values.takenToHospital,
																	hospital: values.hospital,
																	dateAndTimeTakenToHospital: values.dateAndTimeTakenToHospital,
																	personTakenToHospital: values.personTakenToHospital,
																	relationshipToChild: values.relationshipToChild,
																	otherActions: values.otherActions,
																	otherActionsTaken: values.otherActionsTaken
																}));

																setShowAddMedicalPersonnelModal(true);
															}}
															type="button"
															className="add-witness mbtom-0"
														>
															<PlusIcon className="icon" />
															Add medical personnel
														</Button>
													</>
												);
											}}
										</FieldArray>

										<ErrorMessage name="medicalPersonnel">
											{(error) => <div className="form-validation-error">{error}</div>}
										</ErrorMessage>
									</div>
								)}

								<h3>Emergency services</h3>

								<p className="sub-grey-text mtop-10">
									Please record the date and time of contact and which emergency services were contacted
								</p>

								<div className="general-divider mbtom-30"></div>

								<div className="form-group w-60 mbtom-40">
									<label className="label">Were emergency services (ambulance, police, fire) contacted?</label>

									<div className="form-field radio-group">
										<div className="radio-row mbtom-0">
											<Field
												name="emergencyServiceContacted"
												id="emergencyServiceContacted"
												type="radio"
												value={true}
												checked={formik.values.emergencyServiceContacted === true}
												onChange={() => formik.setFieldValue('emergencyServiceContacted', true)}
											/>

											<span className="radio-option">Yes</span>
										</div>

										<div className="radio-row mbtom-0">
											<Field
												name="emergencyServiceContacted"
												id="emergencyServiceContacted"
												type="radio"
												value={false}
												checked={formik.values.emergencyServiceContacted === false}
												onChange={() => formik.setFieldValue('emergencyServiceContacted', false)}
											/>

											<span className="radio-option">No</span>
										</div>

										<ErrorMessage name="emergencyServiceContacted" component="div" className="error-message" />
									</div>
								</div>

								{formik.values.emergencyServiceContacted === true && (
									<div className="form-group w-60 mbtom-40">
										<label>List of emergency services contacted</label>

										<div className="general-divider mbtom-0"></div>

										<FieldArray name="emergencyServices">
											{(props) => {
												const { push, remove, form } = props;
												const { values } = form;
												const { emergencyServices } = values;

												return (
													<>
														{emergencyServices?.length > 0 &&
															emergencyServices.map((item: any, index: number) => {
																return (
																	<div className="incident-detail-row row" key={index}>
																		<div className="col-12 pdl-0">
																			<p className="name">{item.name}</p>
																		</div>

																		<div className="col-4 pdl-0">
																			<p>
																				Contacted <DisplayDateTime>{item.dateAndTime}</DisplayDateTime>{' '}
																			</p>
																		</div>

																		<div className="col-5"></div>

																		<div className="col-3 text-right pdr-0">
																			<Button
																				onClick={() => {
																					remove(index);
																				}}
																				className="btn-text pd-0"
																			>
																				Remove
																			</Button>
																		</div>

																		<div className="col-5 pdl-0">
																			{item.attend ? (
																				<p>
																					Arrived <DisplayDateTime>{item.dateAndTimeAttended}</DisplayDateTime>
																				</p>
																			) : (
																				<p>Did not attend</p>
																			)}
																		</div>
																	</div>
																);
															})}

														<Button
															onClick={() => {
																setIncidentFormData((prevState) => ({
																	...prevState,

																	firstAidAdministered: values.firstAidAdministered,
																	firstAid: values.firstAid,
																	medicationAdministered: values.medicationAdministered,
																	medication: values.medication,
																	medicalPersonnelContacted: values.medicalPersonnelContacted,
																	medicalPersonnel: values.medicalPersonnel,
																	emergencyServiceContacted: values.emergencyServiceContacted,
																	emergencyServices: values.emergencyServices,
																	takenToHospital: values.takenToHospital,
																	hospital: values.hospital,
																	dateAndTimeTakenToHospital: values.dateAndTimeTakenToHospital,
																	personTakenToHospital: values.personTakenToHospital,
																	relationshipToChild: values.relationshipToChild,
																	otherActions: values.otherActions,
																	otherActionsTaken: values.otherActionsTaken
																}));

																setShowAddEmergencyServiceModal(true);
															}}
															type="button"
															className="add-witness mbtom-0"
														>
															<PlusIcon className="icon" />
															Add emergency service
														</Button>
													</>
												);
											}}
										</FieldArray>

										<ErrorMessage name="emergencyServices">
											{(error) => <div className="form-validation-error">{error}</div>}
										</ErrorMessage>
									</div>
								)}

								<h3>Hospital</h3>

								<p className="sub-grey-text mtop-10">
									Please record the date and time the child was taken to hospital and which hospital
								</p>

								<div className="general-divider mbtom-30"></div>

								<div className="form-group w-60 mbtom-40">
									<label className="label">Was the child taken to hospital?</label>

									<div className="form-field radio-group">
										<div className="radio-row mbtom-0">
											<Field
												name="takenToHospital"
												id="takenToHospital"
												type="radio"
												value={true}
												checked={formik.values.takenToHospital === true}
												onChange={() => formik.setFieldValue('takenToHospital', true)}
											/>

											<span className="radio-option">Yes</span>
										</div>

										<div className="radio-row mbtom-0">
											<Field
												name="takenToHospital"
												id="takenToHospital"
												type="radio"
												value={false}
												checked={formik.values.takenToHospital === false}
												onChange={() => formik.setFieldValue('takenToHospital', false)}
											/>

											<span className="radio-option">No</span>
										</div>

										<ErrorMessage name="takenToHospital" component="div" className="error-message" />
									</div>
								</div>

								{formik.values.takenToHospital === true && (
									<>
										<div className="form-group w-60 mbtom-40">
											<label htmlFor="hospital" className="label">
												Which hospital was the child taken to?
											</label>

											<div className="form-field">
												<Field name="hospital" id="hospital" type="text" className="form-input" />

												<ErrorMessage name="hospital" component="div" className="error-message" />
											</div>
										</div>

										<div className="form-group w-60 mbtom-40">
											<label className="label">Date and time they were taken to the hospital</label>

											<DateTimeInput
												dateTimeString={formik.values.dateAndTimeTakenToHospital}
												onChange={(val: string) => formik.setFieldValue('dateAndTimeTakenToHospital', val)}
												excludeFuture
											/>
										</div>

										<div className="form-group w-60 mbtom-40">
											<label htmlFor="personTakenToHospital" className="label">
												Who was responsible for taking the child to the hospital?
											</label>

											<div className="form-field">
												<Field
													name="personTakenToHospital"
													id="personTakenToHospital"
													type="text"
													className="form-input"
												/>

												<ErrorMessage name="personTakenToHospital" component="div" className="error-message" />
											</div>
										</div>

										<div className="form-group w-60 mbtom-40">
											<label htmlFor="relationshipToChild" className="label">
												Relationship to child
											</label>

											<div className="form-field">
												<Field name="relationshipToChild" id="relationshipToChild" type="text" className="form-input" />

												<ErrorMessage name="relationshipToChild" component="div" className="error-message" />
											</div>
										</div>
									</>
								)}

								<h3>Other actions taken</h3>

								<div className="general-divider mbtom-30"></div>

								<div className="form-group w-60 mbtom-30">
									<label className="label">
										Were any other actions taken to respond to this incident, injury, trauma or illness?
									</label>

									<div className="form-field radio-group">
										<div className="radio-row mbtom-0">
											<Field
												name="otherActions"
												id="otherActions"
												type="radio"
												value={true}
												checked={formik.values.otherActions === true}
												onChange={() => formik.setFieldValue('otherActions', true)}
											/>

											<span className="radio-option">Yes</span>
										</div>

										<div className="radio-row mbtom-0">
											<Field
												name="otherActions"
												id="otherActions"
												type="radio"
												value={false}
												checked={formik.values.otherActions === false}
												onChange={() => formik.setFieldValue('otherActions', false)}
											/>

											<span className="radio-option">No</span>
										</div>

										<ErrorMessage name="otherActions" component="div" className="error-message" />
									</div>
								</div>

								{formik.values.otherActions === true && (
									<div className="form-group mbtom-40 w-60">
										<label htmlFor="otherActionsTaken" className="label">
											Please describe any other actions taken to respond to this incident, injury, trauma or illness
										</label>

										<div className="form-field">
											<Field
												as="textarea"
												name="otherActionsTaken"
												id="otherActionsTaken"
												type="text"
												className="form-input"
											/>

											<ErrorMessage name="otherActionsTaken" component="div" className="error-message" />
										</div>
									</div>
								)}
							</div>
						</div>

						<div className="form-button-group">
							<Button type="submit" className="btn-primary submit-btn complaints-submit-page">
								Continue
							</Button>
						</div>

						<GlobalModal
							isOpen={showAddMedicationModal}
							setIsOpen={setShowAddMedicationModal}
							titleText="Add medication"
						>
							<AddMedicationModal incidentFormData={incidentFormData} setIncidentFormData={setIncidentFormData} />
						</GlobalModal>

						<GlobalModal
							isOpen={showAddMedicalPersonnelModal}
							setIsOpen={setShowAddMedicalPersonnelModal}
							titleText="Add medical personnel"
						>
							<AddMedicalPersonnelModal incidentFormData={incidentFormData} setIncidentFormData={setIncidentFormData} />
						</GlobalModal>

						<GlobalModal
							isOpen={showAddEmergencyServiceModal}
							setIsOpen={setShowAddEmergencyServiceModal}
							titleText="Add emergency service"
						>
							<AddEmergencyServiceModal incidentFormData={incidentFormData} setIncidentFormData={setIncidentFormData} />
						</GlobalModal>
					</Form>
				);
			}}
		</Formik>
	);
};

export default StepTwo;
